import React, { useCallback, useRef, useState } from 'react';
import './style.scss';

interface Props {
    value: [number, number];
    onChange: (value: [number, number]) => void;
    min: number;
    max: number;
    step?: number;
    minRange?: number;
    label?: ((value: number) => React.ReactNode) | null;
    className?: string;
}

type ActiveThumb = 0 | 1 | null;

export const BaseRangeSlider: React.FunctionComponent<Props> = ({
    value,
    onChange,
    min,
    max,
    step = 1,
    minRange = 0,
    label,
    className,
}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [activeThumb, setActiveThumb] = useState<ActiveThumb>(null);

    const percentLow = ((value[0] - min) / (max - min)) * 100;
    const percentHigh = ((value[1] - min) / (max - min)) * 100;

    const getValueFromPosition = useCallback(
        (clientX: number) => {
            const track = trackRef.current;
            if (!track) return min;
            const rect = track.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
            let raw = min + ratio * (max - min);
            raw = Math.round(raw / step) * step;
            return Math.max(min, Math.min(max, raw));
        },
        [min, max, step],
    );

    const clampValue = useCallback(
        (thumb: 0 | 1, raw: number): [number, number] => {
            const next: [number, number] = [...value];
            if (thumb === 0) {
                next[0] = Math.min(raw, value[1] - minRange);
                next[0] = Math.max(min, next[0]);
            } else {
                next[1] = Math.max(raw, value[0] + minRange);
                next[1] = Math.min(max, next[1]);
            }
            return next;
        },
        [value, min, max, minRange],
    );

    const closestThumb = useCallback(
        (clientX: number): 0 | 1 => {
            const raw = getValueFromPosition(clientX);
            const distLow = Math.abs(raw - value[0]);
            const distHigh = Math.abs(raw - value[1]);
            return distLow <= distHigh ? 0 : 1;
        },
        [getValueFromPosition, value],
    );

    const handlePointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        const thumb = closestThumb(e.clientX);
        setActiveThumb(thumb);
        const raw = getValueFromPosition(e.clientX);
        onChange(clampValue(thumb, raw));
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (activeThumb === null) return;
        const raw = getValueFromPosition(e.clientX);
        onChange(clampValue(activeThumb, raw));
    };

    const handlePointerUp = (e: React.PointerEvent) => {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
        setActiveThumb(null);
    };

    const handleInputChange = (thumb: 0 | 1) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = Number(e.target.value);
        onChange(clampValue(thumb, raw));
    };

    return (
        <div className={`base-range-slider${className ? ` ${className}` : ''}`}>
            <div
                className="base-range-slider__track"
                ref={trackRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            >
                <div
                    className="base-range-slider__bar"
                    style={{ left: `${percentLow}%`, width: `${percentHigh - percentLow}%` }}
                />

                <div
                    className="base-range-slider__thumb"
                    style={{ left: `${percentLow}%` }}
                />

                <div
                    className="base-range-slider__thumb"
                    style={{ left: `${percentHigh}%` }}
                />
            </div>

            <input
                type="range"
                className="base-range-slider__input"
                min={min}
                max={max - minRange}
                step={step}
                value={value[0]}
                onChange={handleInputChange(0)}
                aria-label="Minimum value"
            />
            <input
                type="range"
                className="base-range-slider__input"
                min={min + minRange}
                max={max}
                step={step}
                value={value[1]}
                onChange={handleInputChange(1)}
                aria-label="Maximum value"
            />
        </div>
    );
};
